import { IconClock, IconLock } from '@tabler/icons-react';
import { Text, Progress, Box, Stack, Group } from '@/components/ui';
import { usePresenceManager } from '@/hooks/usePresenceManager';

/**
 * IdleWarningToast Component
 * Shows a countdown warning at the bottom left when screen lock is approaching
 * Warning threshold is configurable in Presence Settings (default 20 seconds)
 */
export default function IdleWarningToast() {
  const { idleCountdown, isIdle, config } = usePresenceManager();

  const warningThreshold = config.lockWarningThreshold || 20;

  // Don't show if already idle (screen is locked) or countdown is above threshold
  if (isIdle || idleCountdown > warningThreshold || idleCountdown <= 0) {
    return null;
  }

  // Only show if auto-lock is enabled (from persisted store config)
  if (!config.autoLockOnIdle) {
    return null;
  }

  // Calculate progress percentage (inverse - starts full, goes to empty)
  const progressValue = (idleCountdown / warningThreshold) * 100;

  // Determine urgency color based on time remaining
  const getColor = () => {
    if (idleCountdown <= 5) return 'red';
    if (idleCountdown <= 10) return 'orange';
    return 'yellow';
  };

  const color = getColor();

  return (
    <Box
      style={{
        position: 'fixed',
        bottom: 20,
        left: 20,
        zIndex: 9999,
        backgroundColor: 'var(--mantine-color-body)',
        border: `2px solid var(--mantine-color-${color}-6)`,
        borderRadius: 12,
        padding: '16px 20px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
        minWidth: 280,
        animation: 'slideIn 0.3s ease-out',
      }}
    >
      <style>
        {`
          @keyframes slideIn {
            from {
              transform: translateX(-100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.6; }
          }
        `}
      </style>

      <Stack gap="sm">
        <Group gap="sm">
          <Box
            style={{
              padding: 8,
              borderRadius: '50%',
              backgroundColor: `var(--mantine-color-${color}-1)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: idleCountdown <= 10 ? 'pulse 1s infinite' : undefined,
            }}
          >
            <IconLock size={20} color={`var(--mantine-color-${color}-6)`} />
          </Box>
          <Stack gap={2}>
            <Text size="sm" fw={600}>
              Screen will lock soon
            </Text>
            <Text size="xs" c="dimmed">
              Move mouse or press any key to stay active
            </Text>
          </Stack>
        </Group>

        <Group gap="xs" align="center">
          <IconClock size={16} color={`var(--mantine-color-${color}-6)`} />
          <Text size="lg" fw={700} c={color}>
            {idleCountdown}s
          </Text>
          <Progress
            value={progressValue}
            color={color}
            size="sm"
            style={{ flex: 1 }}
            animated={idleCountdown <= 10}
          />
        </Group>
      </Stack>
    </Box>
  );
}
